import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { createSingleFlightRefresh, parseSession } from '~/lib/auth/session';
import { PromotionPoliciesApi } from '@sleed/bizcommerce-api/api/promotion-policies-api';
import { RobotsApi } from '@sleed/bizcommerce-api/api/robots-api';
import { OrdersApi } from '@sleed/bizcommerce-api/api/orders-api';
import { OrderLinesApi } from '@sleed/bizcommerce-api/api/order-lines-api';
import { OrderStatesApi } from '@sleed/bizcommerce-api/api/order-states-api';
import { PaymentTypesApi } from '@sleed/bizcommerce-api/api/payment-types-api';
import { ShippingTypesApi } from '@sleed/bizcommerce-api/api/shipping-types-api';
import { TakeAwayStoresApi } from '@sleed/bizcommerce-api/api/take-away-stores-api';
import { SkroutzApi } from '@sleed/bizcommerce-api/api/skroutz-api';
import { ShippingChargesApi } from '@sleed/bizcommerce-api/api/shipping-charges-api';
import { ShippingTypePostalCodesMapApi } from '@sleed/bizcommerce-api/api/shipping-type-postal-codes-map-api';
import { AccountApi } from '@sleed/bizcommerce-api/api/account-api';
import { CountriesApi } from '@sleed/bizcommerce-api/api/countries-api';
import { CountryRegionsApi } from '@sleed/bizcommerce-api/api/country-regions-api';
import { CitiesApi } from '@sleed/bizcommerce-api/api/cities-api';
import { TaxOfficesApi } from '@sleed/bizcommerce-api/api/tax-offices-api';
import { PostalCodesApi } from '@sleed/bizcommerce-api/api/postal-codes-api';
import { BackInStockSubscriptionsApi } from '@sleed/bizcommerce-api/api/back-in-stock-subscriptions-api';
import { EventSubscriptionsApi } from '@sleed/bizcommerce-api/api/event-subscriptions-api';
import { TaskSchedulesApi } from '@sleed/bizcommerce-api/api/task-schedules-api';
import { ApiKeysApi } from '@sleed/bizcommerce-api/api/api-keys-api';
import { McpPendingActionsApi } from '@sleed/bizcommerce-api/api/mcp-pending-actions-api';
import { McpKeysApi } from '@sleed/bizcommerce-api/api/mcp-keys-api';
import { EmailTemplatesApi } from '@sleed/bizcommerce-api/api/email-templates-api';
import { AvailableDatesApi } from '@sleed/bizcommerce-api/api/available-dates-api';
import { ReportsApi } from '@sleed/bizcommerce-api/api/reports-api';
import { ProductsApi } from '@sleed/bizcommerce-api/api/products-api';
import { ProductAttributesApi } from '@sleed/bizcommerce-api/api/product-attributes-api';
import { CustomersApi } from '@sleed/bizcommerce-api/api/customers-api';
import { CustomerTiersApi } from '@sleed/bizcommerce-api/api/customer-tiers-api';
import { CustomerCategoriesApi } from '@sleed/bizcommerce-api/api/customer-categories-api';
import { CustomerCouponsApi } from '@sleed/bizcommerce-api/api/customer-coupons-api';
import { ReferralsApi } from '@sleed/bizcommerce-api/api/referrals-api';
import { CustomerBranchesApi } from '@sleed/bizcommerce-api/api/customer-branches-api';
import { CustomerAccessRulesApi } from '@sleed/bizcommerce-api/api/customer-access-rules-api';
import { RegisterRequestsApi } from '@sleed/bizcommerce-api/api/register-requests-api';
import { SalesPersonsApi } from '@sleed/bizcommerce-api/api/sales-persons-api';
import { InspectorsApi } from '@sleed/bizcommerce-api/api/inspectors-api';
import { SalesPersonCustomersMapApi } from '@sleed/bizcommerce-api/api/sales-person-customers-map-api';
import { SalesPersonInspectorsMapApi } from '@sleed/bizcommerce-api/api/sales-person-inspectors-map-api';
import { CouponsApi } from '@sleed/bizcommerce-api/api/coupons-api';
import { DiscountCapsApi } from '@sleed/bizcommerce-api/api/discount-caps-api';
import { CustomerTierRulesApi } from '@sleed/bizcommerce-api/api/customer-tier-rules-api';
import { ContestsApi } from '@sleed/bizcommerce-api/api/contests-api';
import { ProductTabsApi } from '@sleed/bizcommerce-api/api/product-tabs-api';
import { ProductTabEntriesApi } from '@sleed/bizcommerce-api/api/product-tab-entries-api';
import { MetricTypesApi } from '@sleed/bizcommerce-api/api/metric-types-api';
import { StatusesApi } from '@sleed/bizcommerce-api/api/statuses-api';
import { CategoriesApi } from '@sleed/bizcommerce-api/api/categories-api';
import { AttributeCategoriesApi } from '@sleed/bizcommerce-api/api/attribute-categories-api';
import { AttributesApi } from '@sleed/bizcommerce-api/api/attributes-api';
import { AttributeValuesApi } from '@sleed/bizcommerce-api/api/attribute-values-api';
import { CategoryAttributesMapApi } from '@sleed/bizcommerce-api/api/category-attributes-map-api';
import { TaxCategoriesApi } from '@sleed/bizcommerce-api/api/tax-categories-api';
import { PriceTypesApi } from '@sleed/bizcommerce-api/api/price-types-api';
import { CurrenciesApi } from '@sleed/bizcommerce-api/api/currencies-api';
import { TaxRulesApi } from '@sleed/bizcommerce-api/api/tax-rules-api';
import { FAQsApi } from '@sleed/bizcommerce-api/api/faqs-api';
import { ProductRelationsApi } from '@sleed/bizcommerce-api/api/product-relations-api';
import { ProductDimensionsApi } from '@sleed/bizcommerce-api/api/product-dimensions-api';
import { ReturnsApi } from '@sleed/bizcommerce-api/api/returns-api';
import { ReturnPolicyApi } from '@sleed/bizcommerce-api/api/return-policy-api';
import { ReturnSettingsApi } from '@sleed/bizcommerce-api/api/return-settings-api';
import { RelationTypesApi } from '@sleed/bizcommerce-api/api/relation-types-api';
import { ManufacturersApi } from '@sleed/bizcommerce-api/api/manufacturers-api';
import { BundlesApi } from '@sleed/bizcommerce-api/api/bundles-api';
import { BuyGuidesApi } from '@sleed/bizcommerce-api/api/buy-guides-api';
import { SeasonsApi } from '@sleed/bizcommerce-api/api/seasons-api';
import { ModelsApi } from '@sleed/bizcommerce-api/api/models-api';
import { WarehousesApi } from '@sleed/bizcommerce-api/api/warehouses-api';
import { ThemeTemplatesApi } from '@sleed/bizcommerce-api/api/theme-templates-api';
import { WishlistTemplatesApi } from '@sleed/bizcommerce-api/api/wishlist-templates-api';
import { FilterTemplateValuesApi } from '@sleed/bizcommerce-api/api/filter-template-values-api';
import { DimensionsApi } from '@sleed/bizcommerce-api/api/dimensions-api';
import { DimensionValuesApi } from '@sleed/bizcommerce-api/api/dimension-values-api';
import { DimenstionValueGroupingApi } from '@sleed/bizcommerce-api/api/dimenstion-value-grouping-api';
import { SizeGuidesApi } from '@sleed/bizcommerce-api/api/size-guides-api';
import { FilterTemplatesApi } from '@sleed/bizcommerce-api/api/filter-templates-api';
import { ProductFiltersApi } from '@sleed/bizcommerce-api/api/product-filters-api';
import { ReviewsApi } from '@sleed/bizcommerce-api/api/reviews-api';
import { RecommendationsApi } from '@sleed/bizcommerce-api/api/recommendations-api';
import { BinaryRepoFilesApi } from '@sleed/bizcommerce-api/api/binary-repo-files-api';
import { BinaryRepoImagesApi } from '@sleed/bizcommerce-api/api/binary-repo-images-api';
import { LanguagesApi } from '@sleed/bizcommerce-api/api/languages-api';
import { UsersApi } from '@sleed/bizcommerce-api/api/users-api';
import { UserRolesApi } from '@sleed/bizcommerce-api/api/user-roles-api';
import { SettingsApi } from '@sleed/bizcommerce-api/api/settings-api';
import { SitesApi } from '@sleed/bizcommerce-api/api/sites-api';
import { SystemLogApi } from '@sleed/bizcommerce-api/api/system-log-api';
import { ActivityLogsApi } from '@sleed/bizcommerce-api/api/activity-logs-api';
import { SitemapPagesApi } from '@sleed/bizcommerce-api/api/sitemap-pages-api';
import { UrlRedirectsApi } from '@sleed/bizcommerce-api/api/url-redirects-api';
import { SmsTemplatesApi } from '@sleed/bizcommerce-api/api/sms-templates-api';
import { FileManagerApi } from '@sleed/bizcommerce-api/api/file-manager-api';
import { SocialMediaApi } from '@sleed/bizcommerce-api/api/social-media-api';
import { LogoApi } from '@sleed/bizcommerce-api/api/logo-api';
import { CustomMessagesGroupsApi } from '@sleed/bizcommerce-api/api/custom-messages-groups-api';
import { VideoGroupsApi } from '@sleed/bizcommerce-api/api/video-groups-api';
import { NotificationCategoriesApi } from '@sleed/bizcommerce-api/api/notification-categories-api';
import { EventTagsApi } from '@sleed/bizcommerce-api/api/event-tags-api';
import { EventCategoriesApi } from '@sleed/bizcommerce-api/api/event-categories-api';
import { VideoCategoriesApi } from '@sleed/bizcommerce-api/api/video-categories-api';
import { CustomMessagesApi } from '@sleed/bizcommerce-api/api/custom-messages-api';
import { EventLocationsApi } from '@sleed/bizcommerce-api/api/event-locations-api';
import { EventsApi } from '@sleed/bizcommerce-api/api/events-api';
import { ContactFormsApi } from '@sleed/bizcommerce-api/api/contact-forms-api';
import { PushSubscriptionsApi } from '@sleed/bizcommerce-api/api/push-subscriptions-api';
import { VideosApi } from '@sleed/bizcommerce-api/api/videos-api';
import { NotificationsApi } from '@sleed/bizcommerce-api/api/notifications-api';
import { CustomContentApi } from '@sleed/bizcommerce-api/api/custom-content-api';
import { ContentPagesApi } from '@sleed/bizcommerce-api/api/content-pages-api';
import { BannerGroupsApi } from '@sleed/bizcommerce-api/api/banner-groups-api';
import { BannersApi } from '@sleed/bizcommerce-api/api/banners-api';
import { CustomerGroupsApi } from '@sleed/bizcommerce-api/api/customer-groups-api';
import { MenuItemsApi } from '@sleed/bizcommerce-api/api/menu-items-api';
import { FooterItemsApi } from '@sleed/bizcommerce-api/api/footer-items-api';
import { NewsEntriesApi } from '@sleed/bizcommerce-api/api/news-entries-api';
import { NewsGroupsApi } from '@sleed/bizcommerce-api/api/news-groups-api';
import { NewsCategoriesApi } from '@sleed/bizcommerce-api/api/news-categories-api';
import { NewsEntriesTagsApi } from '@sleed/bizcommerce-api/api/news-entries-tags-api';
import { ContributorsApi } from '@sleed/bizcommerce-api/api/contributors-api';
import { ContributorCategoriesApi } from '@sleed/bizcommerce-api/api/contributor-categories-api';
import { ContributorGroupApi } from '@sleed/bizcommerce-api/api/contributor-group-api';
import { PointRangesApi } from '@sleed/bizcommerce-api/api/point-ranges-api';
import { PointProductsApi } from '@sleed/bizcommerce-api/api/point-products-api';
import { PointCustomerApi } from '@sleed/bizcommerce-api/api/point-customer-api';
import { PointsPerEntityApi } from '@sleed/bizcommerce-api/api/points-per-entity-api';
import { ImportExportApi } from '@sleed/bizcommerce-api/api/import-export-api';
import { NewsletterSubscriptionsApi } from '@sleed/bizcommerce-api/api/newsletter-subscriptions-api';
import { GiftCardsOrderApi } from '@sleed/bizcommerce-api/api/gift-cards-order-api';
import { BookingsApi } from '@sleed/bizcommerce-api/api/bookings-api';
import { BookingTypesApi } from '@sleed/bizcommerce-api/api/booking-types-api';
import { BookingExceptionsApi } from '@sleed/bizcommerce-api/api/booking-exceptions-api';
import { RepresentativesApi } from '@sleed/bizcommerce-api/api/representatives-api';
import { RepresentativeExceptionsApi } from '@sleed/bizcommerce-api/api/representative-exceptions-api';
import { BookingReportsApi } from '@sleed/bizcommerce-api/api/booking-reports-api';
import i18n from '~/lib/i18n';
import { getAccessToken, getRefreshToken, sessionStorageAdapter } from '~/lib/auth/storage';

/**
 * API transport for the generated client classes. Mirrors the legacy admin's
 * api_helper.js: Bearer request interceptor + single-flight 401 refresh.
 *
 * In dev, baseURL is empty so requests are relative (`/v1/...`) and Vite proxies
 * them to the local API (no CORS). In prod, `__BW_ENV__.apiBaseUrl` points at the
 * real API origin.
 */
interface BwEnv {
	apiBaseUrl?: string;
}

export function getApiBaseUrl(): string {
	const env = (globalThis as { __BW_ENV__?: BwEnv }).__BW_ENV__;
	return env?.apiBaseUrl ?? '';
}

export const apiAxios = axios.create({ baseURL: getApiBaseUrl() });

// Exported for the assistant's fetch-based SSE layer (lib/assistant/adminAssistantApi.ts), which
// bypasses the axios interceptors: it shares this SAME single-flight refresh on a 401 so a stream
// open and concurrent axios calls never race duplicate refreshes.
export const refreshSession = createSingleFlightRefresh({
	post: () => apiAxios.post('/v1/account/refreshtoken', { at: getAccessToken(), rt: getRefreshToken() }).then(r => r.data),
	onSuccess: data => {
		const session = parseSession(data);
		if (session) sessionStorageAdapter.save(session);
	}
});

apiAxios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	const token = getAccessToken();
	// Respect a per-call Authorization override (e.g. the impersonation token used for
	// Orders/AdminSave, whose CanCreateOrderFromAdmin policy needs the impersonation-actor
	// claims). Only fall back to the logged-in admin token when no header was set explicitly.
	if (token && !config.headers.has('Authorization')) config.headers.set('Authorization', `Bearer ${token}`);
	// Pin the API culture to the active i18n language (default 'el', the data-entry
	// default). Without this the browser's Accept-Language (often en-US) makes the
	// server treat writes as a non-default language and route them through the
	// multi-language translation path — which on Create inserts a PromotionPolicyML
	// row against an unresolved FK and fails with 409 (see api_helper.js parity).
	config.headers.set('Accept-Language', i18n.language || 'el');
	return config;
});

apiAxios.interceptors.response.use(
	response => response,
	async (error: AxiosError) => {
		const cfg = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
		const url = cfg?.url ?? '';
		const isAuthEndpoint = url.endsWith('/account/login') || url.endsWith('/account/refreshtoken');

		if (error.response?.status === 401 && cfg && !isAuthEndpoint && !cfg._retry) {
			cfg._retry = true;
			try {
				await refreshSession(); // single-flight; concurrent 401s share it
			} catch {
				sessionStorageAdapter.clear();
				if (globalThis.location?.pathname !== '/login') globalThis.location?.assign('/login');
				return Promise.reject(error);
			}
			const token = getAccessToken();
			if (token) cfg.headers.set('Authorization', `Bearer ${token}`);
			return apiAxios(cfg);
		}
		return Promise.reject(error);
	}
);

// The nohoisted axios copy differs nominally from the generated client's; assert
// against the constructor's expected type. basePath '' keeps request paths relative
// (`/v1/...`) so the dev proxy handles them.
type ApiAxiosArg = ConstructorParameters<typeof PromotionPoliciesApi>[2];
export const promotionPoliciesApi = new PromotionPoliciesApi(undefined, '', apiAxios as ApiAxiosArg);
export const ordersApi = new OrdersApi(undefined, '', apiAxios as ApiAxiosArg);
export const orderLinesApi = new OrderLinesApi(undefined, '', apiAxios as ApiAxiosArg);
export const orderStatesApi = new OrderStatesApi(undefined, '', apiAxios as ApiAxiosArg);
export const paymentTypesApi = new PaymentTypesApi(undefined, '', apiAxios as ApiAxiosArg);
export const shippingTypesApi = new ShippingTypesApi(undefined, '', apiAxios as ApiAxiosArg);
export const takeAwayStoresApi = new TakeAwayStoresApi(undefined, '', apiAxios as ApiAxiosArg);
export const skroutzApi = new SkroutzApi(undefined, '', apiAxios as ApiAxiosArg);
export const shippingChargesApi = new ShippingChargesApi(undefined, '', apiAxios as ApiAxiosArg);
export const shippingTypePostalCodesMapApi = new ShippingTypePostalCodesMapApi(undefined, '', apiAxios as ApiAxiosArg);
export const accountApi = new AccountApi(undefined, '', apiAxios as ApiAxiosArg);
export const countriesApi = new CountriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const countryRegionsApi = new CountryRegionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const citiesApi = new CitiesApi(undefined, '', apiAxios as ApiAxiosArg);
export const taxOfficesApi = new TaxOfficesApi(undefined, '', apiAxios as ApiAxiosArg);
export const postalCodesApi = new PostalCodesApi(undefined, '', apiAxios as ApiAxiosArg);
export const eventSubscriptionsApi = new EventSubscriptionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const backInStockSubscriptionsApi = new BackInStockSubscriptionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const taskSchedulesApi = new TaskSchedulesApi(undefined, '', apiAxios as ApiAxiosArg);
export const apiKeysApi = new ApiKeysApi(undefined, '', apiAxios as ApiAxiosArg);
export const mcpPendingActionsApi = new McpPendingActionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const mcpKeysApi = new McpKeysApi(undefined, '', apiAxios as ApiAxiosArg);
export const emailTemplatesApi = new EmailTemplatesApi(undefined, '', apiAxios as ApiAxiosArg);
export const availableDatesApi = new AvailableDatesApi(undefined, '', apiAxios as ApiAxiosArg);
export const reportsApi = new ReportsApi(undefined, '', apiAxios as ApiAxiosArg);
export const productsApi = new ProductsApi(undefined, '', apiAxios as ApiAxiosArg);
export const productAttributesApi = new ProductAttributesApi(undefined, '', apiAxios as ApiAxiosArg);
export const customersApi = new CustomersApi(undefined, '', apiAxios as ApiAxiosArg);
export const customerTiersApi = new CustomerTiersApi(undefined, '', apiAxios as ApiAxiosArg);
export const customerCategoriesApi = new CustomerCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const customerCouponsApi = new CustomerCouponsApi(undefined, '', apiAxios as ApiAxiosArg);
export const referralsApi = new ReferralsApi(undefined, '', apiAxios as ApiAxiosArg);
export const customerBranchesApi = new CustomerBranchesApi(undefined, '', apiAxios as ApiAxiosArg);
export const customerAccessRulesApi = new CustomerAccessRulesApi(undefined, '', apiAxios as ApiAxiosArg);
export const registerRequestsApi = new RegisterRequestsApi(undefined, '', apiAxios as ApiAxiosArg);
export const salesPersonsApi = new SalesPersonsApi(undefined, '', apiAxios as ApiAxiosArg);
export const inspectorsApi = new InspectorsApi(undefined, '', apiAxios as ApiAxiosArg);
export const salesPersonCustomersMapApi = new SalesPersonCustomersMapApi(undefined, '', apiAxios as ApiAxiosArg);
export const salesPersonInspectorsMapApi = new SalesPersonInspectorsMapApi(undefined, '', apiAxios as ApiAxiosArg);
export const couponsApi = new CouponsApi(undefined, '', apiAxios as ApiAxiosArg);
export const discountCapsApi = new DiscountCapsApi(undefined, '', apiAxios as ApiAxiosArg);
export const customerTierRulesApi = new CustomerTierRulesApi(undefined, '', apiAxios as ApiAxiosArg);
export const contestsApi = new ContestsApi(undefined, '', apiAxios as ApiAxiosArg);
export const productTabsApi = new ProductTabsApi(undefined, '', apiAxios as ApiAxiosArg);
export const productTabEntriesApi = new ProductTabEntriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const statusesApi = new StatusesApi(undefined, '', apiAxios as ApiAxiosArg);
export const metricTypesApi = new MetricTypesApi(undefined, '', apiAxios as ApiAxiosArg);
export const categoriesApi = new CategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const attributeCategoriesApi = new AttributeCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const attributesApi = new AttributesApi(undefined, '', apiAxios as ApiAxiosArg);
export const attributeValuesApi = new AttributeValuesApi(undefined, '', apiAxios as ApiAxiosArg);
export const categoryAttributesMapApi = new CategoryAttributesMapApi(undefined, '', apiAxios as ApiAxiosArg);
export const taxCategoriesApi = new TaxCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const priceTypesApi = new PriceTypesApi(undefined, '', apiAxios as ApiAxiosArg);
export const currenciesApi = new CurrenciesApi(undefined, '', apiAxios as ApiAxiosArg);
export const taxRulesApi = new TaxRulesApi(undefined, '', apiAxios as ApiAxiosArg);
export const faqsApi = new FAQsApi(undefined, '', apiAxios as ApiAxiosArg);
export const productRelationsApi = new ProductRelationsApi(undefined, '', apiAxios as ApiAxiosArg);
export const productDimensionsApi = new ProductDimensionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const returnsApi = new ReturnsApi(undefined, '', apiAxios as ApiAxiosArg);
export const returnPolicyApi = new ReturnPolicyApi(undefined, '', apiAxios as ApiAxiosArg);
export const returnSettingsApi = new ReturnSettingsApi(undefined, '', apiAxios as ApiAxiosArg);
export const relationTypesApi = new RelationTypesApi(undefined, '', apiAxios as ApiAxiosArg);
export const manufacturersApi = new ManufacturersApi(undefined, '', apiAxios as ApiAxiosArg);
export const buyGuidesApi = new BuyGuidesApi(undefined, '', apiAxios as ApiAxiosArg);
export const bundlesApi = new BundlesApi(undefined, '', apiAxios as ApiAxiosArg);
export const seasonsApi = new SeasonsApi(undefined, '', apiAxios as ApiAxiosArg);
export const modelsApi = new ModelsApi(undefined, '', apiAxios as ApiAxiosArg);
export const warehousesApi = new WarehousesApi(undefined, '', apiAxios as ApiAxiosArg);
export const themeTemplatesApi = new ThemeTemplatesApi(undefined, '', apiAxios as ApiAxiosArg);
export const wishlistTemplatesApi = new WishlistTemplatesApi(undefined, '', apiAxios as ApiAxiosArg);
export const filterTemplateValuesApi = new FilterTemplateValuesApi(undefined, '', apiAxios as ApiAxiosArg);
export const dimensionsApi = new DimensionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const dimensionValuesApi = new DimensionValuesApi(undefined, '', apiAxios as ApiAxiosArg);
export const dimensionValueGroupingApi = new DimenstionValueGroupingApi(undefined, '', apiAxios as ApiAxiosArg);
export const sizeGuidesApi = new SizeGuidesApi(undefined, '', apiAxios as ApiAxiosArg);
export const filterTemplatesApi = new FilterTemplatesApi(undefined, '', apiAxios as ApiAxiosArg);
export const productFiltersApi = new ProductFiltersApi(undefined, '', apiAxios as ApiAxiosArg);
export const reviewsApi = new ReviewsApi(undefined, '', apiAxios as ApiAxiosArg);
export const recommendationsApi = new RecommendationsApi(undefined, '', apiAxios as ApiAxiosArg);
export const binaryRepoImagesApi = new BinaryRepoImagesApi(undefined, '', apiAxios as ApiAxiosArg);
export const binaryRepoFilesApi = new BinaryRepoFilesApi(undefined, '', apiAxios as ApiAxiosArg);
export const languagesApi = new LanguagesApi(undefined, '', apiAxios as ApiAxiosArg);
export const usersApi = new UsersApi(undefined, '', apiAxios as ApiAxiosArg);
export const userRolesApi = new UserRolesApi(undefined, '', apiAxios as ApiAxiosArg);
export const settingsApi = new SettingsApi(undefined, '', apiAxios as ApiAxiosArg);
export const sitesApi = new SitesApi(undefined, '', apiAxios as ApiAxiosArg);
export const systemLogApi = new SystemLogApi(undefined, '', apiAxios as ApiAxiosArg);
export const activityLogsApi = new ActivityLogsApi(undefined, '', apiAxios as ApiAxiosArg);
export const sitemapPagesApi = new SitemapPagesApi(undefined, '', apiAxios as ApiAxiosArg);
export const robotsApi = new RobotsApi(undefined, '', apiAxios as ApiAxiosArg);
export const urlRedirectsApi = new UrlRedirectsApi(undefined, '', apiAxios as ApiAxiosArg);
export const smsTemplatesApi = new SmsTemplatesApi(undefined, '', apiAxios as ApiAxiosArg);
export const fileManagerApi = new FileManagerApi(undefined, '', apiAxios as ApiAxiosArg);
export const socialMediaApi = new SocialMediaApi(undefined, '', apiAxios as ApiAxiosArg);
export const logoApi = new LogoApi(undefined, '', apiAxios as ApiAxiosArg);
export const customMessagesGroupsApi = new CustomMessagesGroupsApi(undefined, '', apiAxios as ApiAxiosArg);
export const videoGroupsApi = new VideoGroupsApi(undefined, '', apiAxios as ApiAxiosArg);
export const notificationCategoriesApi = new NotificationCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const eventTagsApi = new EventTagsApi(undefined, '', apiAxios as ApiAxiosArg);
export const eventCategoriesApi = new EventCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const videoCategoriesApi = new VideoCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const customMessagesApi = new CustomMessagesApi(undefined, '', apiAxios as ApiAxiosArg);
export const eventLocationsApi = new EventLocationsApi(undefined, '', apiAxios as ApiAxiosArg);
export const eventsApi = new EventsApi(undefined, '', apiAxios as ApiAxiosArg);
export const contactFormsApi = new ContactFormsApi(undefined, '', apiAxios as ApiAxiosArg);
export const pushSubscriptionsApi = new PushSubscriptionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const videosApi = new VideosApi(undefined, '', apiAxios as ApiAxiosArg);
export const notificationsApi = new NotificationsApi(undefined, '', apiAxios as ApiAxiosArg);
export const customContentApi = new CustomContentApi(undefined, '', apiAxios as ApiAxiosArg);
export const contentPagesApi = new ContentPagesApi(undefined, '', apiAxios as ApiAxiosArg);
export const bannerGroupsApi = new BannerGroupsApi(undefined, '', apiAxios as ApiAxiosArg);
export const bannersApi = new BannersApi(undefined, '', apiAxios as ApiAxiosArg);
export const customerGroupsApi = new CustomerGroupsApi(undefined, '', apiAxios as ApiAxiosArg);
export const menuItemsApi = new MenuItemsApi(undefined, '', apiAxios as ApiAxiosArg);
export const footerItemsApi = new FooterItemsApi(undefined, '', apiAxios as ApiAxiosArg);
export const newsEntriesApi = new NewsEntriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const newsGroupsApi = new NewsGroupsApi(undefined, '', apiAxios as ApiAxiosArg);
export const newsCategoriesApi = new NewsCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const newsEntriesTagsApi = new NewsEntriesTagsApi(undefined, '', apiAxios as ApiAxiosArg);
export const contributorsApi = new ContributorsApi(undefined, '', apiAxios as ApiAxiosArg);
export const contributorCategoriesApi = new ContributorCategoriesApi(undefined, '', apiAxios as ApiAxiosArg);
export const contributorGroupApi = new ContributorGroupApi(undefined, '', apiAxios as ApiAxiosArg);
export const pointRangesApi = new PointRangesApi(undefined, '', apiAxios as ApiAxiosArg);
export const pointProductsApi = new PointProductsApi(undefined, '', apiAxios as ApiAxiosArg);
export const pointCustomerApi = new PointCustomerApi(undefined, '', apiAxios as ApiAxiosArg);
export const pointsPerEntityApi = new PointsPerEntityApi(undefined, '', apiAxios as ApiAxiosArg);
export const importExportApi = new ImportExportApi(undefined, '', apiAxios as ApiAxiosArg);
export const newsletterSubscriptionsApi = new NewsletterSubscriptionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const giftCardsOrderApi = new GiftCardsOrderApi(undefined, '', apiAxios as ApiAxiosArg);
export const bookingsApi = new BookingsApi(undefined, '', apiAxios as ApiAxiosArg);
export const bookingTypesApi = new BookingTypesApi(undefined, '', apiAxios as ApiAxiosArg);
export const bookingExceptionsApi = new BookingExceptionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const representativesApi = new RepresentativesApi(undefined, '', apiAxios as ApiAxiosArg);
export const representativeExceptionsApi = new RepresentativeExceptionsApi(undefined, '', apiAxios as ApiAxiosArg);
export const bookingReportsApi = new BookingReportsApi(undefined, '', apiAxios as ApiAxiosArg);
